import React, {useState, useEffect} from 'react';
import {Box, Center, Flex, Spinner, Textarea, VStack, useToast} from '@chakra-ui/react';
import {TextG12, TextG14, TextG16, TextG18} from '../../../components/typography';
import Modal from '../../../components/Modals/Modal';
import {
  useConversationStarterPrompts,
  useConversationStarters,
  useUpsertConversationStarter,
  ConversationStarterPrompt,
} from '../../../hooks/useConversationStarters';
import styled from 'styled-components';

const SaveButton = styled.button<{$disabled: boolean}>`
  width: 100%;
  padding: 14px;
  border-radius: 28px;
  border: none;
  font-family: Gordita;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.$disabled ? '#E5E5E5' : '#E02F84')};
  box-shadow: ${(props) => (props.$disabled ? 'none' : '0 -4px 0 0 rgba(0, 0, 0, 0.25) inset')};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  &:active {
    transform: ${(props) => (props.$disabled ? 'none' : 'scale(0.98)')};
  }
`;

const MAX_CHARS = 150;

// -- Answer Sub-modal --
interface AnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt: ConversationStarterPrompt;
  currentAnswer: string;
}

const ConversationStarterAnswerModal = ({
  isOpen,
  onClose,
  prompt,
  currentAnswer,
}: AnswerModalProps) => {
  const [text, setText] = useState('');
  const upsertMutation = useUpsertConversationStarter();
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setText(currentAnswer);
    }
  }, [isOpen, currentAnswer]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= MAX_CHARS) {
      setText(e.target.value);
    }
  };

  const handleSave = () => {
    upsertMutation.mutate(
      {prompt_id: prompt.id, answer: text.trim()},
      {
        onSuccess: () => {
          toast({title: 'Answer saved!', status: 'success', duration: 2000});
          onClose();
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.message || 'Could not save your answer';
          toast({title: msg, status: 'error', duration: 3000});
        },
      },
    );
  };

  return (
    <Modal
      show={isOpen}
      parentCallback={() => onClose()}
      closeButton={false}
      header={
        <Flex align="center" w="100%">
          <Box as="button" onClick={onClose} mr={2} p={1} _active={{opacity: 0.7}}>
            <i className="fi fi-rr-arrow-left" style={{fontSize: '16px'}} />
          </Box>
          <TextG14 fontWeight="700" textAlign="center" flex={1} mt="0">
            Your Answer
          </TextG14>
          <Box w="24px" />
        </Flex>
      }
      modalPadding="16px 20px 24px">
      <VStack spacing={5} align="stretch" minH="300px" justify="space-between">
        <Box>
          <Box
            bg="#F9F9F9"
            borderRadius="12px"
            p={4}
            mb={3}
            boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08);"
            border="2px solid #E9E9E9">
            <TextG14 mt="0" color="#1A1A1A" lineHeight="20px" fontStyle="italic">
              {prompt.text}
            </TextG14>
          </Box>
          <Box position="relative">
            <Textarea
              value={text}
              onChange={handleChange}
              placeholder="Type your answer..."
              fontFamily="Gordita"
              fontSize="14px"
              lineHeight="20px"
              p="14px 16px"
              border="1px solid #E5E5E5"
              borderRadius="12px"
              resize="none"
              minH="120px"
              _focus={{borderColor: '#1A1A1A', boxShadow: 'none'}}
              _placeholder={{color: '#C4C4C4'}}
            />
            <Flex justify="flex-end" mt={1} pr={1}>
              <TextG12 mt="0" color="#C4C4C4">
                {text.length}/{MAX_CHARS}
              </TextG12>
            </Flex>
          </Box>
        </Box>

        <SaveButton $disabled={upsertMutation.isLoading} onClick={handleSave}>
          {upsertMutation.isLoading ? 'Saving...' : 'Save'}
        </SaveButton>
      </VStack>
    </Modal>
  );
};

// -- Main Conversation Starters list modal --
interface ConversationStartersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConversationStartersModal = ({isOpen, onClose}: ConversationStartersModalProps) => {
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const {data: prompts = [], isLoading: loadingPrompts} = useConversationStarterPrompts();
  const {data: answers = []} = useConversationStarters();

  // Build a lookup: prompt_id → answer text
  const answerMap = new Map(answers.map((a) => [a.prompt_id, a.answer]));

  const selectedPrompt = prompts.find((p) => p.id === selectedPromptId);

  if (selectedPrompt) {
    return (
      <ConversationStarterAnswerModal
        isOpen={isOpen}
        onClose={() => setSelectedPromptId(null)}
        prompt={selectedPrompt}
        currentAnswer={answerMap.get(selectedPrompt.id) || ''}
      />
    );
  }

  return (
    <Modal
      show={isOpen}
      parentCallback={() => onClose()}
      closeButton
      closeButtonPosition="right"
      header={
        <TextG18 fontWeight="700" textAlign="center" w="100%" mt="0">
          Conversation Starters
        </TextG18>
      }
      modalPadding="8px 0 24px">
      {loadingPrompts ? (
        <Center py={12}>
          <Spinner size="lg" color="#E02F84" />
        </Center>
      ) : (
        <VStack spacing={0} align="stretch">
          {prompts.map((prompt, index) => (
            <Box
              key={prompt.id}
              as="button"
              w="100%"
              textAlign="left"
              px={5}
              py={4}
              borderBottom={index < prompts.length - 1 ? '1px solid #F0F0F0' : 'none'}
              onClick={() => setSelectedPromptId(prompt.id)}
              _active={{bg: '#FAFAFA'}}>
              <Flex justify="space-between" align="center">
                <Box flex={1} pr={3}>
                  <TextG16 fontWeight="500" mt="0" lineHeight="24px">
                    {prompt.text}...
                  </TextG16>
                  {answerMap.get(prompt.id) && (
                    <TextG14 mt="4px" color="#202020" noOfLines={1} lineHeight="22px">
                      {answerMap.get(prompt.id)}
                    </TextG14>
                  )}
                </Box>
                <i
                  className="fi fi-rr-angle-small-right"
                  style={{fontSize: '16px', color: '#C4C4C4'}}
                />
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </Modal>
  );
};

export default ConversationStartersModal;
